import { Flex, Text, useToast } from '@chakra-ui/react'
import React, { useEffect } from 'react'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import IconCallStop from '~/assets/svgs/icon-call-stop.svg?react'
import IconMerge from '~/assets/svgs/icon-merge.svg?react'
import IconPhoneHold from '~/assets/svgs/icon-phone-hold.svg?react'
import IconPhoneMute from '~/assets/svgs/icon-phone-mute.svg?react'
import IconPhoneSwap from '~/assets/svgs/icon-phone-swap.svg?react'
import IconPhoneTransfer from '~/assets/svgs/icon-phone-transfer.svg?react'
import IconPlus from '~/assets/svgs/icon-plus.svg?react'
import IconSplit from '~/assets/svgs/icon-split.svg?react'
import { useModalState } from '~/atoms/ModalState'
import { CallMode, useSettingsState } from '~/atoms/SettingsState'
import { easeInOutTransition } from '~/components/CommonStyles'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import useCloudwatchLogger from '~/hooks/useCloudwatchLogger'
import { useNetworkConnectivity } from '~/hooks/useNetworkConnectivity'
import { usePhoneEndpoint } from '~/hooks/usePhone'
import { ApplicationRoutes } from '~/hooks/useRouting'
import { Size } from '~/types/ThemeTypes'
import {
  convertCallDurationNoMilliseconds,
  formatPhoneNumber,
} from '~/utils/formatter'

import { PhoneCall, PhoneCallStatus } from '@mangovoice/types'

import { phoneColorKeys } from './componentStyles/_colors'
import {
  CallBlockVariants,
  useCallBlockStyle,
} from './componentStyles/CallBlock.style'
import { FeedbackToast } from './FeedbackToast'

const RingbackToneUri = '/ringbacktone.wav'

interface CollapsableWrapperProps {
  collapsed?: boolean
}
export const CollapsableWrapper = styled.div<CollapsableWrapperProps>`
  display: ${({ collapsed }) => (collapsed ? 'none' : 'default')};
  ${easeInOutTransition}
`

const getCallBlockVariant = (isRinging: boolean, isOnHold: boolean) => {
  if (isRinging) {
    return CallBlockVariants.Ringing
  }
  if (isOnHold) {
    return CallBlockVariants.OnHold
  }
  return CallBlockVariants.Connected
}

interface CallBlockProps {
  calls: PhoneCall[]
  callUuid: string
  isRinging: boolean
  isActiveCall: boolean
  hasActiveCall: boolean
  startTime: Date | null
  number: string
  onEndCall: (callUuid: string) => void
  onMute: (callUuid: string) => void
  isMuted: boolean
  onHold: (callUuid: string) => Promise<void>
  isOnHold: boolean
  isCollapsed?: boolean
  isOnline: boolean
  onSwitchCall: (callUuid: string) => Promise<void>
  onTransferCall: (callUuid: string) => void
  onNavigateToWebPhone: () => void
}
const CallBlock = ({
  calls,
  callUuid,
  isRinging,
  isActiveCall,
  hasActiveCall,
  startTime,
  number,
  onEndCall,
  onMute,
  isMuted,
  onHold,
  isOnHold,
  isCollapsed,
  isOnline,
  onSwitchCall,
  onTransferCall,
  onNavigateToWebPhone,
}: CallBlockProps) => {
  const [callDuration, setCallDuration] = React.useState<string>('0:00')
  const {
    settings: {
      webPhone: { hasCallWaiting },
    },
  } = useSettingsState()
  // Create an interval to track call duration and update label during call.
  // Depends on callStartTime
  useEffect(() => {
    const updateCallDuration = () => {
      if (!startTime) {
        return
      }
      const callDurationInSeconds =
        (new Date().getTime() - startTime.getTime()) / 1000
      const callDurationString = convertCallDurationNoMilliseconds(
        callDurationInSeconds,
      )
      setCallDuration(callDurationString)
    }

    const interval = setInterval(() => updateCallDuration(), 1000)
    return () => clearInterval(interval)
  }, [startTime])

  const handleEndCall = () => {
    onEndCall(callUuid)
  }
  const handleMute = () => {
    onMute(callUuid)
  }
  const handleHold = async () => {
    await onHold(callUuid)
  }
  const handleSwitchCall = async () => {
    await onSwitchCall(callUuid)
  }
  const handleTransferCall = () => {
    onTransferCall(callUuid)
  }

  const handleBadgeClick = () => {
    if (isCollapsed) {
      if (isActiveCall) {
        onNavigateToWebPhone()
      } else {
        onSwitchCall(callUuid)
      }
    }
  }

  const getCallStatusText = () => {
    if (!isOnline) {
      return 'Reconnecting...'
    }
    if (isRinging) {
      if (isCollapsed) return 'Ring'
      return 'Ringing'
    }
    if (isOnHold) {
      if (isCollapsed) return 'Hold'
      return 'On Hold'
    }
    if (isCollapsed) {
      return `${callDuration}`
    }
    return `Connected ${callDuration}`
  }
  const variant = getCallBlockVariant(isRinging, isOnHold)
  const styles = useCallBlockStyle({ variant })

  const badgeCursor = isCollapsed ? 'pointer' : 'default'
  if (calls.length > 1 && !isActiveCall && !isOnHold && hasCallWaiting) {
    return null
  }
  return (
    <Flex sx={styles.container} paddingX={isCollapsed ? '0.25rem' : undefined}>
      {isActiveCall && isRinging && (
        <audio id="ringback" src={RingbackToneUri} autoPlay loop />
      )}
      <Text
        sx={styles.statusBadge}
        onClick={handleBadgeClick}
        cursor={badgeCursor}
      >
        {getCallStatusText()}
      </Text>

      <CollapsableWrapper collapsed={isCollapsed}>
        <Text sx={styles.number}>{number}</Text>
        <Flex sx={styles.controlBar}>
          <QuickActionButton
            variant={QuickActionButtonVariants.Destructive_100}
            size={Size.sm}
            actionIcon={IconCallStop}
            tooltipText="End Call"
            onClick={handleEndCall}
            iconProps={{ boxSize: '20px' }}
          />
          <QuickActionButton
            variant={QuickActionButtonVariants.Default_150}
            size={Size.sm}
            actionIcon={IconPhoneMute}
            tooltipText="Mute Call"
            isActive={isMuted}
            onClick={handleMute}
            iconProps={{ boxSize: '24px' }}
          />

          {isActiveCall && !isRinging && (
            <QuickActionButton
              variant={QuickActionButtonVariants.Default_150}
              size={Size.sm}
              actionIcon={IconPhoneHold}
              tooltipText="Hold Call"
              isActive={isOnHold}
              onClick={handleHold}
              iconProps={{ boxSize: '24px' }}
            />
          )}

          {!isActiveCall && (
            <>
              <QuickActionButton
                variant={QuickActionButtonVariants.Default_150}
                size={Size.sm}
                actionIcon={IconPhoneSwap}
                tooltipText="Switch to Call"
                onClick={handleSwitchCall}
                iconProps={{ boxSize: '28px' }}
              />
              {hasActiveCall && (
                <QuickActionButton
                  variant={QuickActionButtonVariants.Default_150}
                  size={Size.sm}
                  actionIcon={IconPhoneTransfer}
                  tooltipText="Warm Transfer"
                  onClick={handleTransferCall}
                  iconProps={{ boxSize: '24px' }}
                />
              )}
            </>
          )}
        </Flex>
      </CollapsableWrapper>
    </Flex>
  )
}

interface ConferenceBlockProps {
  startTime?: Date
  numberOfParticipants: number
  onEndConference: () => void
  onMuteConference: () => void
  isMuted: boolean
  onHoldConference: () => Promise<void>
  isOnHold: boolean
  isCollapsed?: boolean
  onNavigateToWebPhone: () => void
}
const ConferenceBlock = ({
  startTime,
  numberOfParticipants,
  onEndConference,
  onMuteConference,
  isMuted,
  onHoldConference,
  isOnHold,
  isCollapsed,
  onNavigateToWebPhone,
}: ConferenceBlockProps) => {
  const [callDuration, setCallDuration] = React.useState<string>('0:00')

  // Create an interval to track call duration and update label during call.
  // Depends on callStartTime
  useEffect(() => {
    const updateCallDuration = () => {
      if (!startTime) {
        return
      }
      const callDurationInSeconds =
        (new Date().getTime() - startTime.getTime()) / 1000
      const callDurationString = convertCallDurationNoMilliseconds(
        callDurationInSeconds,
      )
      setCallDuration(callDurationString)
    }

    const interval = setInterval(() => updateCallDuration(), 1000)
    return () => clearInterval(interval)
  }, [startTime])

  const handleEndConference = () => {
    onEndConference()
  }
  const handleMuteConference = () => {
    onMuteConference()
  }
  const handleHoldConference = async () => {
    await onHoldConference()
  }

  const handleBadgeClick = () => {
    if (isCollapsed) {
      onNavigateToWebPhone()
    }
  }

  const getCallStatusText = () => {
    if (isOnHold) {
      if (isCollapsed) return 'Hold'
      return 'On Hold'
    }
    if (isCollapsed) {
      return `${callDuration}`
    }
    return `Connected ${callDuration}`
  }
  const variant = getCallBlockVariant(false, isOnHold)
  const styles = useCallBlockStyle({ variant })

  const badgeCursor = isCollapsed ? 'pointer' : 'default'

  return (
    <Flex sx={styles.container} paddingX={isCollapsed ? '0.25rem' : undefined}>
      <Text
        sx={styles.statusBadge}
        onClick={handleBadgeClick}
        cursor={badgeCursor}
      >
        {getCallStatusText()}
      </Text>

      <CollapsableWrapper collapsed={isCollapsed}>
        <Text sx={styles.number}>{numberOfParticipants} Participants</Text>
        <Flex sx={styles.controlBar}>
          <QuickActionButton
            variant={QuickActionButtonVariants.Destructive_100}
            size={Size.sm}
            actionIcon={IconCallStop}
            tooltipText="End Call"
            onClick={handleEndConference}
            iconProps={{ boxSize: '20px' }}
          />
          <QuickActionButton
            variant={QuickActionButtonVariants.Default_150}
            size={Size.sm}
            actionIcon={IconPhoneMute}
            tooltipText="Mute Call"
            isActive={isMuted}
            onClick={handleMuteConference}
            iconProps={{ boxSize: '24px' }}
          />
          <QuickActionButton
            variant={QuickActionButtonVariants.Default_150}
            size={Size.sm}
            actionIcon={IconPhoneHold}
            tooltipText="Hold Call"
            isActive={isOnHold}
            onClick={handleHoldConference}
            iconProps={{ boxSize: '24px' }}
          />
        </Flex>
      </CollapsableWrapper>
    </Flex>
  )
}

const { ActiveCallBlock: CallBlockColors } = phoneColorKeys.Phone

export const ActiveCallsBlock = ({
  isCollapsed,
}: {
  isCollapsed?: boolean
}) => {
  const { logger } = useCloudwatchLogger('CallBlock')
  const feedbackToast = useToast({
    duration: 15000,
    position: 'bottom-left',
    containerStyle: {
      width: '10.75rem',
      height: '7.25rem',
      minWidth: '10.75rem',
    },
    render: () => (
      <FeedbackToast
        onClose={handleCloseFeedbackToast}
        onOpenFeedbackModal={() => {
          setFeedbackModalState({ open: true })
        }}
      />
    ),
  })

  const {
    state: {
      phoneCalls: {
        active: { activeCall },
        list: { callList },
        conference: {
          conferencedCalls,
          isMuted: conferenceIsMuted,
          isOnHold: conferenceIsOnHold,
          startTime: conferenceStartTime,
        },
        isAtMaxCallLimit,
      },
    },
    actions: {
      endCall,
      muteCall,
      holdCall,
      switchCall,
      attendedTransferCall,
      mergeCalls,
      splitCalls,
      endConferenceCall,
      toggleMuteConference,
      toggleHoldConference,
    },
  } = usePhoneEndpoint({
    onFinalCallEnd: () => {
      if (Math.random() < 0.1) {
        logger?.info('call_fb_displayed')
        feedbackToast({})
      }
    },
  })

  const navigate = useNavigate()

  const {
    feedbackModal: [, setFeedbackModalState],
    callModal: [, setCallModalState],
  } = useModalState()
  const isOnline = useNetworkConnectivity({
    playConnectionChangeSound: !!activeCall,
    reportAnalytics: true,
  })

  const handleNewCallClick = () => {
    setCallModalState({ open: true, overrideCallMode: CallMode.WEBPHONE })
  }

  const handleCloseFeedbackToast = () => {
    feedbackToast.closeAll()
  }

  const handleTransferCall = (callUuid: string) => {
    if (!activeCall) return
    attendedTransferCall(callUuid, activeCall.callUuid)
  }

  const handleMergeCalls = () => {
    const callUuids = Array.from(callList.keys())
    mergeCalls(callUuids)
  }
  const handleSplitCalls = () => {
    splitCalls()
  }
  const handleMuteConference = () => {
    toggleMuteConference()
  }
  const handleHoldConference = async () => {
    await toggleHoldConference()
  }
  const handleEndConference = () => {
    endConferenceCall()
  }

  if (callList.size === 0) {
    return <div />
  }

  const getCallBlocks = () => {
    if (conferencedCalls) {
      return [
        <ConferenceBlock
          key="conference"
          startTime={conferenceStartTime}
          numberOfParticipants={conferencedCalls.length}
          onEndConference={handleEndConference}
          onMuteConference={handleMuteConference}
          isMuted={conferenceIsMuted}
          onHoldConference={handleHoldConference}
          isOnHold={conferenceIsOnHold}
          isCollapsed={isCollapsed}
          onNavigateToWebPhone={() => navigate(ApplicationRoutes.WEB_PHONE)}
        />,
      ]
    }

    const calls = Array.from(callList.values())
    return calls.map((call) => {
      const formattedNumber = formatPhoneNumber(call.number)
      return (
        <CallBlock
          calls={calls}
          key={call.callUuid}
          isOnline={isOnline}
          callUuid={call.callUuid}
          isRinging={call.status === PhoneCallStatus.RINGING}
          isActiveCall={activeCall?.callUuid === call.callUuid}
          hasActiveCall={activeCall !== undefined}
          startTime={call.startTime}
          number={formattedNumber}
          onEndCall={endCall}
          onMute={muteCall}
          isMuted={call.isMuted}
          onHold={holdCall}
          isOnHold={call.isOnHold}
          isCollapsed={isCollapsed}
          onSwitchCall={switchCall}
          onTransferCall={handleTransferCall}
          onNavigateToWebPhone={() => navigate(ApplicationRoutes.WEB_PHONE)}
        />
      )
    })
  }

  // HACK: Something breaks when using this specific style config, couldn't figure it out
  // const styles = useActiveCallsBlockStyle()
  return (
    <Flex flexDirection="column">
      <CollapsableWrapper collapsed={isCollapsed}>
        <Flex
          justify="space-between"
          align="center"
          borderBottom="1px solid"
          borderColor={CallBlockColors.CallBlockHeader.Border}
          py="4px"
          px="12px"
          // sx={styles.header}
        >
          <Text
            // sx={styles.text}
            noOfLines={1}
            textStyle="Body2"
          >
            Active Call
          </Text>
          {!isAtMaxCallLimit && (
            <QuickActionButton
              variant={QuickActionButtonVariants.Default_150}
              size={Size.sm}
              actionIcon={IconPlus}
              tooltipText="New Call"
              onClick={handleNewCallClick}
            />
          )}
          {isAtMaxCallLimit && !conferencedCalls && (
            <QuickActionButton
              variant={QuickActionButtonVariants.Default_150}
              size={Size.sm}
              actionIcon={IconMerge}
              tooltipText="Merge Calls"
              onClick={handleMergeCalls}
            />
          )}
          {isAtMaxCallLimit && conferencedCalls && (
            <QuickActionButton
              variant={QuickActionButtonVariants.Default_100}
              size={Size.sm}
              actionIcon={IconSplit}
              tooltipText="End Conference"
              onClick={handleSplitCalls}
            />
          )}
        </Flex>
      </CollapsableWrapper>
      {getCallBlocks()}
    </Flex>
  )
}
